<template>
    <page-loading-indicator
        v-if="state.isLoading"
        :number-of-breadcrumb-elements="5"
        subtitle
        actions
        :number-of-next-actions="0"
    />
    <page-wrapper v-else>
        <page-header>
            <template v-slot:breadcrumbs>
                <q-breadcrumbs-el :label="state.contract.customer.getContactName()" :to="{ name: 'contact-detail', params: { id: contactId } }" data-test="breadcrumb:contact-detail" />
                <q-breadcrumbs-el :label="$tc('common.term.contract', 2)" :to="{ name: 'contact-detail', params: { id: contactId }}" data-test="breadcrumb:contracts" />
                <q-breadcrumbs-el :label="state.contract.currentContractNumber" :to="{ name: 'contract-detail', params: { id: state.contract.id }}" data-test="breadcrumb:contract-detail" />
                <!--<q-breadcrumbs-el :label="$tc('common.premium-invoice.premium-invoice', 2)" :to="{ name: 'contract-detail-premium-invoices', params: { contactId: contactId, id: state.contract.id, tab: 'premium-invoices', type: 'invoices' }}" data-test="breadcrumb:premium-invoices" />-->
                <q-breadcrumbs-el :label="$tc('common.premium-invoice.invoice', 2)" :to="{ name: 'contract-detail-premium-invoices', params: { contactId: contactId, id: state.contract.id, tab: 'premium-invoices', type: 'invoices' }}" data-test="breadcrumb:premium-invoices" />
                <q-breadcrumbs-el :label="state.premiumInvoice.formattedIdentifier" :to="{ name: 'premium-invoice-detail', params: { id: state.contract.id }}" data-test="breadcrumb:premium-invoice-detail" />
            </template>

            <h1>{{ state.premiumInvoice.formattedIdentifier }} <span v-if="state.premiumInvoice.hasIdentifier" class="subtitle">({{ state.premiumInvoice.totalPosition.formattedBillingPeriod }})</span></h1>

            <template v-slot:actions>
                <actions
                    :actions="state.premiumInvoice.actions"
                    :number-of-next-best-actions="0"
                    translation-base-key="common.actions.premium-invoice"
                    @click="handleActions"
                />

                <!-- Delete PremiumInvoice -->
                <form-dialog
                    ref="deleteDialog"
                    double-confirm
                    @submit="handleDelete"
                >
                    <template v-slot:title>{{ $tc('common.premium-invoice.delete-premium-invoice', 1) }}</template>
                    <template v-slot:default>
                        <i18n path="common.premium-invoice.delete-premium-invoice--confirm--temp-component-interpolation-count" tag="p">
                            <template v-if="state.premiumInvoice" v-slot:invoiceId><b>{{ state.premiumInvoice.formattedIdentifier }}</b></template>
                        </i18n>
                    </template>
                </form-dialog>
            </template>
        </page-header>

        <div class="row print-row-lg q-col-gutter-md entity-info-wrapper">
            <grid-card
                :columns="{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 4,
                    xl: 4,
                }"
                class="entity-info premium-invoice-info"
                data-test="wrapper:premium-invoice-info"
            >
                <dl class="q-mb-none">
                    <dt>{{ $tc('common.premium-invoice.invoice-number', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.invoice_number }]" data-test="text:invoice-number">{{ state.premiumInvoice.invoice_number || '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.invoice-date', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.formattedInvoiceDate }]" data-test="text:invoice-date">{{ state.premiumInvoice.formattedInvoiceDate || '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.document-recipient.document-recipient', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.document_recipient }]" data-test="text:document-recipient">{{ state.premiumInvoice.document_recipient ? $tc(`common.premium-invoice.document-recipient.${kebabCase(state.premiumInvoice.document_recipient)}`, 1) : '–' }}</dd>
                </dl>
            </grid-card>

            <grid-card
                :columns="{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 8,
                    xl: 8,
                }"
                class="entity-info total-amount-info"
                data-test="wrapper:total-amount-info"
            >
                <dl class="q-mb-none">
                    <dt>{{ $tc('common.premium-invoice.billing-period', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !(!!state.premiumInvoice.totalPosition.premium_from && !!state.premiumInvoice.totalPosition.premium_to) }]" data-test="text:billing-period">{{ (!!state.premiumInvoice.totalPosition.premium_from && !!state.premiumInvoice.totalPosition.premium_to) ? state.premiumInvoice.totalPosition.formattedBillingPeriod : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.premium-kind.premium-kind', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.premium_kind }]" data-test="text:premium-kind">{{ state.premiumInvoice.totalPosition.premium_kind ? $tc(`common.premium-invoice.premium-kind.${kebabCase(state.premiumInvoice.totalPosition.premium_kind)}`, 1) : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.premium-type.premium-type', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.premium_type }]" data-test="text:premium-type">{{ state.premiumInvoice.totalPosition.premium_type ? $tc(`common.premium-invoice.premium-type.${kebabCase(state.premiumInvoice.totalPosition.premium_type)}`, 1) : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.sector-code', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.sectorCode.code }]" data-test="text:sector-code">{{ state.premiumInvoice.totalPosition.sectorCode.code ? state.premiumInvoice.totalPosition.formattedIgb2bSectorCode : '–' }}</dd>
                </dl>

                <dl class="q-mb-none">
                    <dt>{{ $tc('common.premium-invoice.annual-premium-amount', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.annual_premium_amount }]" data-test="text:annual-premium-amount">{{ state.premiumInvoice.totalPosition.annual_premium_amount ? state.premiumInvoice.totalPosition.formattedAnnualPremiumAmount : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.instalment-surcharge', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.instalment_surcharge }]" data-test="text:instalment-surcharge">{{ state.premiumInvoice.totalPosition.instalment_surcharge ? state.premiumInvoice.totalPosition.formattedInstalmentSurcharge : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.taxes-statutory-charges', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.taxes_statutory_charges }]" data-test="text:taxes-statutory-charges">{{ state.premiumInvoice.totalPosition.taxes_statutory_charges ? state.premiumInvoice.totalPosition.formattedTaxesStatutoryCharges : '–' }}</dd>
                    <br>

                    <dt>{{ $tc('common.premium-invoice.invoiced-amount', 1) }}: </dt>
                    <dd :class="[{ 'additional-info': !state.premiumInvoice.totalPosition.invoiced_amount }]" data-test="text:invoiced-amount">{{ state.premiumInvoice.totalPosition.invoiced_amount ? state.premiumInvoice.totalPosition.formattedInvoicedAmount : '–' }}</dd>
                </dl>
            </grid-card>

            <div v-if="state.premiumInvoice.detailPositions.length" class="col-xs-12 q-mt-md">
                <h2>{{ $tc('common.premium-invoice.invoice-detail', 2) }}</h2>

                <q-table
                    :columns="state.columns"
                    :data="state.premiumInvoice.detailPositions"
                    flat
                    square
                    dense
                />
            </div>

            <div class="col-xs-12">
                <h2>{{ $tc('common.premium-invoice.file.file', 2) }}</h2>
                <!-- Files -->
                <template v-if="state.premiumInvoice?.files?.length">
                    <!-- TODO improvement @MTR: Create file library component. -->
                    <div class="file-library-wrapper">
                        <q-card
                            v-for="file in state.premiumInvoice.files"
                            :key="`premium-invoice-file-${file.id}`"
                            class="file-library-item"
                            square
                            flat
                            bordered
                            data-test="item:premium-invoice-files"
                        >
                            <q-card-section horizontal>
                                <q-item class="items-start">
                                    <q-item-section side>
                                        <q-avatar square>
                                            <q-icon
                                                :name="getFileTypeIcon(file.mime_type || null)"
                                                size="sm"
                                                color="secondary"
                                            />
                                        </q-avatar>
                                    </q-item-section>

                                    <q-item-section>
                                        <q-item-label class="file-name" data-test="text:premium-invoice-file-name">{{ file.name }}</q-item-label>
                                        <q-item-label v-if="file.size" caption data-test="text:premium-invoice-file-size">{{ humanStorageSize(file.size) }}</q-item-label>
                                    </q-item-section>
                                </q-item>

                                <q-card-actions vertical>
                                    <q-btn
                                        class="do-not-print"
                                        color="primary"
                                        size="sm"
                                        flat
                                        round
                                        icon="mib-cloud-download"
                                        @click="file.download()"
                                    >
                                        <q-tooltip :delay="1000" :offset="[0, 10]">{{ $tc('common.term.download-item', 1, { item: file.name }) }}</q-tooltip>
                                    </q-btn>
                                </q-card-actions>
                            </q-card-section>
                        </q-card>
                    </div>
                </template>
            </div>
        </div>
    </page-wrapper>
</template>

<script>
import { onBeforeMount, reactive, ref } from 'vue'
import { PremiumInvoice } from '@/models/premiumInvoice'
import Actions from '@/components/Actions'
import FormDialog from '@/libs/form/components/FormDialog'
import { useRouter } from '@/composables/router'
import { useQuasar } from '@/composables/quasar'
import { useI18n } from '@/composables/i18n'
import { Contract } from '@/models/contract'
import { kebabCase } from 'lodash'
import { getFileTypeIcon, humanStorageSize } from '@/helpers/file'

export default {
    name: 'PremiumInvoiceDetail',
    meta () {
        return {
            title: !this.state.premiumInvoice ? `${this.$t('common.term.loading')} …` : this.state.premiumInvoice?.formattedIdentifier,
        }
    },
    components: {
        Actions,
        FormDialog,
    },
    props: {
        contactId: {
            type: String,
            required: true,
        },
        contractId: {
            type: String,
            required: true,
        },
        premiumInvoiceId: {
            type: String,
            required: true,
        },
    },
    setup (props) {
        // Composables
        const router = useRouter()
        const $q = useQuasar()
        const { tc } = useI18n()

        // Data
        const state = reactive({
            isLoading: true,
            premiumInvoice: null,
            columns: [
                {
                    name: 'billing_period',
                    label: tc('common.premium-invoice.billing-period', 1),
                    field: row => row.formattedBillingPeriod,
                    sortable: true,
                    required: true,
                    align: 'left',
                },
                {
                    name: 'record_sub_type',
                    label: tc('common.premium-invoice.premium-kind.premium-kind', 1),
                    field: row => tc(`common.premium-invoice.premium-kind.${kebabCase(row.premium_kind)}`, 1),
                    sortable: true,
                    align: 'left',
                },
                {
                    name: 'record_content_type',
                    label: tc('common.premium-invoice.premium-type.premium-type', 1),
                    field: row => tc(`common.premium-invoice.premium-type.${kebabCase(row.premium_type)}`, 1),
                    sortable: true,
                    align: 'left',
                },
                {
                    name: 'sector_code',
                    label: tc('common.premium-invoice.sector-code', 1),
                    field: row => row.formattedIgb2bSectorCode,
                    sortable: true,
                    align: 'left',
                },
                {
                    name: 'annual_premium_amount',
                    label: tc('common.premium-invoice.annual-premium-amount', 1),
                    field: row => row.formattedAnnualPremiumAmount,
                    sortable: true,
                    align: 'left',
                    classes: 'text-right text-bold monospace',
                },
                {
                    name: 'invoiced_amount',
                    label: tc('common.premium-invoice.invoiced-amount', 1),
                    field: row => row.formattedInvoicedAmount,
                    sortable: true,
                    align: 'left',
                    classes: 'text-right text-bold monospace',
                },
            ],
        })

        // Template refs
        const deleteDialog = ref(null)

        // Functions
        async function fetchObjects () {
            state.contract = await Contract.objects.get(props.contractId)
            state.premiumInvoice = await PremiumInvoice.objects.get(props.premiumInvoiceId)
            state.isLoading = false
        }
        function handleActions (action) {
            switch (action.key) {
                case PremiumInvoice.action.UPDATE:
                    router.push({ name: 'premium-invoice-update', params: { contactId: props.contactId, contractId: props.contractId, premiumInvoiceId: state.premiumInvoice.id } })
                    break
                case PremiumInvoice.action.DELETE:
                    deleteDialog.value.open()
                    break
            }
        }
        function handleDelete () {
            state.premiumInvoice.delete()
                .then(() => {
                    deleteDialog.value.close()
                    router.replace({ name: 'contract-detail', params: { contactId: props.contactId, id: props.contractId, tab: 'premium-invoices' } })

                    $q.notify({
                        type: 'positive',
                        message: tc('common.notifications.premium-invoice.premium-invoice-deleted-success', 1),
                    })
                })
                .catch(error => {
                    deleteDialog.value.showFormErrorMessage(error)
                    deleteDialog.value.resetFormSubmitStatus()
                })
        }

        // Lifecycle hooks
        onBeforeMount(() => {
            fetchObjects()
        })

        return {
            // Data
            state,

            // Template refs
            deleteDialog,

            // Functions
            handleActions,
            handleDelete,
            kebabCase,
            getFileTypeIcon,
            humanStorageSize,
        }
    },
}
</script>
